import type { DeploymentRecord } from '@/services/deploymentService';
import { ACCESS_NOTE, STUCK_HINT, retryLine, useRunProgressView } from '@/hooks/useRunProgressView';
import type { ProgressSnapshot } from '@/utils/deployProgressPoller';
import { formatCount, formatPlan, shortenResourceAddress } from '@/utils/deployProgress';
import { groupCounts } from '@/utils/deployStatus';
import { DeployProgressBar, SegmentedBar } from './DeployProgressBar';
import './deployments.css';

interface RowProgressProps {
  record: DeploymentRecord;
  /** The run's progress from the shared poller. Absent while the run has no task yet. */
  snapshot?: ProgressSnapshot;
}

/**
 * A running deploy's progress as one line under its row in a table: the phase,
 * the bar, how much is done, how long the phase has taken, and the resource
 * that finished last. A group deploy then reads as rows filling up.
 *
 * The line is one fixed height whatever it holds, so the row does not jump as
 * the run moves from "Starting" to a measured bar.
 */
export function RowProgress({ record, snapshot }: RowProgressProps) {
  const view = useRunProgressView(record, snapshot);
  const { label, hint, phase, progress, percent, hasCount, showBar, stuck, retryIn } = view;

  if (retryIn !== null) {
    return (
      <div className="deploy-rowline">
        <span className="deploy-progress__phase deploy-progress__phase--retry" title={hint}>
          {label}
        </span>
        <span className="deploy-rowline__text numeric">{retryLine(retryIn)}</span>
        {record.errorTitle && (
          <span className="deploy-rowline__rest" title={record.errorSummary || record.errorTitle}>
            The first attempt failed: {record.errorTitle}
          </span>
        )}
      </div>
    );
  }

  const last = progress?.lastResource && phase !== 'done' ? progress.lastResource : '';
  return (
    <div className="deploy-rowline">
      <span className={`deploy-progress__phase${stuck ? ' deploy-progress__phase--stuck' : ''}`} title={hint}>
        {label}
      </span>
      {showBar && <DeployProgressBar percent={percent} size="sm" label={`${record.clientName} deploy progress`} />}
      {hasCount && progress && percent !== null && (
        <span className="deploy-rowline__text numeric" title={progress.plan ? formatPlan(progress.plan) : undefined}>
          {percent}% · {formatCount(progress.done)} of {formatCount(progress.total)} resources
        </span>
      )}
      {view.phaseTime && <span className="deploy-rowline__text numeric">{view.phaseTime}</span>}
      {progress && progress.errors > 0 && (
        <span className="deploy-rowline__text deploy-progress__errors" title={progress.lastError}>
          {progress.errors} {progress.errors === 1 ? 'error' : 'errors'}
        </span>
      )}
      {view.nearAccessLimit && (
        <span className="deploy-rowline__text deploy-rowline__warn" title={ACCESS_NOTE}>
          Close to the 60 minute limit
        </span>
      )}
      {last && (
        <span className="deploy-rowline__rest" title={last}>
          last: <span className="mono">{shortenResourceAddress(last, record.clientName)}</span>
        </span>
      )}
      {stuck && (
        <span className="deploy-rowline__rest" title={STUCK_HINT}>
          {STUCK_HINT}
        </span>
      )}
      {phase === 'done' && !stuck && <span className="deploy-rowline__rest">Finishing up</span>}
    </div>
  );
}

interface GroupRowProgressProps {
  summary: DeploymentRecord;
  /** The clients' own runs. Absent until the first read of them comes back. */
  members?: DeploymentRecord[];
}

/** A running group deploy's line in the history: its clients as one bar cut by status, and the counts. */
export function GroupRowProgress({ summary, members }: GroupRowProgressProps) {
  const counts = groupCounts(summary, members);
  return (
    <div className="deploy-rowline">
      <SegmentedBar
        total={counts.total}
        segments={[
          { key: 'succeeded', label: 'Succeeded', count: counts.succeeded },
          { key: 'failed', label: 'Failed', count: counts.failed },
          { key: 'running', label: 'Running', count: counts.running },
          { key: 'waiting', label: 'Waiting', count: counts.waiting },
        ]}
      />
      <span className="deploy-rowline__text numeric">
        {counts.done} of {counts.total} clients done
      </span>
      <span className="deploy-rowline__text deploy-count deploy-count--succeeded numeric">
        {counts.succeeded} succeeded
      </span>
      <span className="deploy-rowline__text deploy-count deploy-count--failed numeric">{counts.failed} failed</span>
      <span className="deploy-rowline__text deploy-count deploy-count--running numeric">{counts.running} running</span>
      {counts.waiting > 0 && (
        <span className="deploy-rowline__text deploy-count deploy-count--waiting numeric">
          {counts.waiting} waiting
        </span>
      )}
    </div>
  );
}

export default RowProgress;
